import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { trpc } from "@editor/utils/trpc";

import EditableFunnelName from "@/features/funnel/components/EditableFunnelName";
import { PublishButton } from "@/features/next-gen/components/PublishButton";
import EditablePageNameWithDropdown from "@/features/react-editor/components/navbar/EditablePageNameWithDropdown";
import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import { useProjectPresences } from "@/features/react-editor/components/ProjectWebsocketProvider";
import { useCurrentFile } from "@/features/react-editor/utils/useCurrentFile";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  ArrowLeft,
  History,
  MessageSquare,
  SquareArrowOutUpRight,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import usePublishedSiteUrls from "../../hooks/usePublishedSiteUrls";
import { useEditorStoreActions, usePanelView } from "../../store/editorStore";
import { DeploymentStatusTag } from "../DeploymentStatusTag";
import DebugPanel from "./DebugPanel";
import FunnelSettingsMenu from "./FunnelSettingsMenu";
import PageSettingsMenu from "./PageSettingsMenu";

function ReactEditorNavbar() {
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const { project } = useCurrentProjectContext();
  const file = useCurrentFile();
  const location = useLocation();
  const navigate = useNavigate();
  const [isRenamingFunnel, setIsRenamingFunnel] = React.useState(false);
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);

  // Get route params to determine which data to fetch
  const { funnelId } = useParams();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const presences = useProjectPresences({
    pageId: pageId ?? undefined,
    funnelId: funnelId ?? undefined,
  });

  const isFunnelRoute = Boolean(project?.id && funnelId);

  // Conditionally fetch funnel data if we're on a funnel route
  const { data: funnel } = trpc.funnel.find.useQuery(
    project?.id && funnelId
      ? {
          projectId: project.id,
          funnelId,
        }
      : skipToken,
  );

  const { data: page } = trpc.nextJSProject.getPage.useQuery(
    project?.id && pageId
      ? {
          projectId: project.id,
          id: pageId,
        }
      : skipToken,
  );

  const displayName = React.useMemo(() => {
    if (funnelId && funnel) {
      return funnel.name;
    }
    if (pageId && page) {
      return page.pageRoute;
    }
    return null;
  }, [funnelId, funnel, pageId, page]);

  let pageRouteToUseForPreview: string | undefined = undefined;
  if (isFunnelRoute) {
    const routeOfFirstPageInFunnel = funnel?.funnelSteps.find((step) => {
      return Boolean(step.file);
    })?.file?.pageRoute;

    pageRouteToUseForPreview = routeOfFirstPageInFunnel ?? undefined;
  } else {
    pageRouteToUseForPreview = file?.pageRoute ?? undefined;
  }

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: pageRouteToUseForPreview
      ? [pageRouteToUseForPreview]
      : [],
  });

  const publishedSiteUrl = publishedSiteUrls[0];

  const isViewableLive = React.useMemo(() => {
    if (isFunnelRoute) {
      // For funnel routes, only viewable if the whole funnel is published
      return (
        funnel?.funnelSteps.every((step) => {
          if (!step.file) {
            return false;
          }
          return Boolean(step.file.lastPublishedFileVersionId);
        }) ?? false
      );
    } else {
      // For page routes, only viewable if the current file is published
      return Boolean(file?.lastPublishedFileVersionId);
    }
  }, [isFunnelRoute, funnel?.funnelSteps, file?.lastPublishedFileVersionId]);

  // Smart navigation logic for back button
  const handleBackNavigation = React.useCallback(() => {
    const currentPath = location.pathname;
    // Check if we have meaningful browser history by checking if location.key exists
    // location.key is undefined on the initial load, indicating no history
    const hasHistory = location.key !== "default";

    if (!project) {
      navigate("/shops");
      return;
    }

    // If we're in a funnel route, go back to funnels list
    if (currentPath.includes("/funnels") && funnelId) {
      navigate(`/shops/${project.id}/funnels`);
      return;
    }

    // If we're in a site route, first check if we can go back to previous page, otherwise go to homepage
    // TODO: go to /site tab when it's ready for v2
    if (currentPath.includes("/chat") && pageId) {
      if (hasHistory) {
        navigate(-1);
      } else {
        navigate(`/shops/${project.id}`);
      }

      return;
    }
  }, [location.pathname, location.key, navigate, project, funnelId, pageId]);

  const isSiteRoute = location.pathname.includes("/site");

  const isFunnelEditor = funnelId && funnel && project;
  const isPageEditor = pageId && page && project;

  const fileIdsToPublish = React.useMemo(() => {
    if (isFunnelEditor) {
      return funnel?.funnelSteps
        .map((step) => step.file?.id)
        .filter((id): id is string => Boolean(id));
    }
    return [page?.id].filter((id): id is string => Boolean(id));
  }, [isFunnelEditor, funnel, page]);

  const renderDisplayName = () => {
    if (isFunnelEditor) {
      return (
        <EditableFunnelName
          funnel={funnel}
          projectId={project.id}
          isRenaming={isRenamingFunnel}
          setIsRenaming={setIsRenamingFunnel}
          inputClassName="max-w-[200px]"
          titleClassName="text-muted text-base font-medium cursor-pointer hover:text-default transition-colors line-clamp-1 rounded-md px-2 py-1 hover:bg-selectable-hover transition-colors"
        />
      );
    }

    if (isPageEditor) {
      return (
        <EditablePageNameWithDropdown
          page={page}
          projectId={project.id}
          isPageSelectionPopoverOpen={isPageSelectionPopoverOpen}
          setIsPageSelectionPopoverOpen={setIsPageSelectionPopoverOpen}
          onPageSelect={(selectedPage) => {
            navigate(`/shops/${project.id}/chat?pageId=${selectedPage.id}`);
            setIsPageSelectionPopoverOpen(false);
          }}
        />
      );
    }

    return (
      <div className="text-muted text-base font-medium">
        {displayName ?? ""}
      </div>
    );
  };

  return (
    <>
      <div className="flex gap-3 w-full justify-between">
        <div
          className={twMerge(
            "max-w-[500px] w-full flex flex-row items-center justify-between",
            isSiteRoute && "max-w-1/3",
          )}
        >
          <div className="flex flex-row items-center justify-between w-full gap-3">
            <div className="flex flex-row items-center gap-2">
              <IconButton
                variant="secondary"
                size="base"
                onClick={handleBackNavigation}
                icon={<ArrowLeft className="text-muted" size={16} />}
              />
              {renderDisplayName()}
              {isFunnelEditor && Boolean(funnel) && (
                <DeploymentStatusTag
                  variant="funnel"
                  pages={funnel?.funnelSteps
                    .map((step) => step.file)
                    .filter((file): file is FileWithLatestFileVersion =>
                      Boolean(file),
                    )}
                />
              )}
              {page && Boolean(page) && (
                <DeploymentStatusTag variant="page" file={page} />
              )}
            </div>
            <div className="flex flex-row items-center gap-2">
              {page && (
                <IconButton
                  variant="secondary"
                  size="base"
                  UNSAFE_className={twMerge(
                    panelView === "history" &&
                      "bg-primary/10 border border-primary hover:bg-primary/10",
                  )}
                  icon={
                    <History
                      className={twMerge(
                        "text-muted",
                        panelView === "history" && "text-primary",
                      )}
                      size={16}
                    />
                  }
                  onClick={() =>
                    setPanelView(panelView === "history" ? "chat" : "history")
                  }
                />
              )}
              <IconButton
                variant="secondary"
                size="base"
                UNSAFE_className={twMerge(
                  panelView === "manageChats" &&
                    "bg-primary/10 border border-primary hover:bg-primary/10",
                )}
                icon={
                  <MessageSquare
                    className={twMerge(
                      "text-muted",
                      panelView === "manageChats" && "text-primary",
                    )}
                    size={16}
                  />
                }
                onClick={() =>
                  setPanelView(
                    panelView === "manageChats" ? "chat" : "manageChats",
                  )
                }
              />
              {page && <PageSettingsMenu />}
              {isFunnelEditor && (
                <FunnelSettingsMenu
                  funnel={funnel}
                  setIsRenamingFunnel={setIsRenamingFunnel}
                />
              )}
            </div>
          </div>
        </div>
        <ResponsiveControls />
        <div className="ml-auto flex gap-3">
          <DebugPanel />
          {presences.length > 0 && (
            <div className="px-2 flex items-center">
              {presences.map((presence) => (
                <Tooltip
                  key={presence.userId}
                  content={
                    <div className="flex flex-col gap-1 items-center p-1">
                      <span className="text-sm font-medium">
                        {presence.userName}
                      </span>
                      <span className="text-xs">is also editing</span>
                    </div>
                  }
                  side="bottom"
                  sideOffset={0}
                  triggerAsChild
                >
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[14px] text-white select-none border border-white hover:rotate-15 transition-transform duration-300"
                    style={{
                      backgroundColor: presence.color,
                      marginRight: "-4px",
                    }}
                  >
                    {presence.userName.charAt(0).toUpperCase()}
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
          <Button
            variant="secondary"
            size="base"
            to={publishedSiteUrl}
            target="_blank"
            layoutClassName="w-full flex-1"
            title={publishedSiteUrl}
            disabled={!isViewableLive}
          >
            <div className="flex items-center gap-2">
              <SquareArrowOutUpRight size={16} /> View Live
            </div>
          </Button>
          <PublishButton
            routeType={isFunnelRoute ? "funnel" : "page"}
            publishedSiteUrl={publishedSiteUrl}
            fileIdsToPublish={fileIdsToPublish}
            isViewableLive={isViewableLive}
          />
        </div>
      </div>
    </>
  );
}

export default ReactEditorNavbar;
